/* global BigInt */
import { Button, Modal } from '@themesberg/react-bootstrap'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { getPayAllProgress, payAllReward } from '../../../../../controller/api/__loto__/reward'

function formatVnd(amountStr) {
  try {
    const n = BigInt(amountStr || '0')
    return new Intl.NumberFormat('vi-VN').format(n)
  } catch {
    return amountStr || '0'
  }
}

export default function PayRewardAll(props) {
  const [cookies] = useCookies(['lottmekong'])

  const [done, setDone] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [progress, setProgress] = useState({ pendingCount: 0, pendingAmount: '0' })
  const [progressComplete, setProgressComplete] = useState(false)
  const prevShow = useRef(props.show)
  const batchTotalCountRef = useRef(0)
  const batchTotalNetRef = useRef('0')

  const _fetchProgress = useCallback(async () => {
    const response = await getPayAllProgress(cookies.lottmekong)
    if (!response) {
      return
    }
    const applyResponse = (r) => {
      setProgress({
        pendingCount: Number(r.pendingCount || 0),
        pendingAmount: typeof r.pendingAmount === 'string' ? r.pendingAmount : String(r.pendingAmount || '0'),
      })
      return r.isDone === true
    }

    const isDoneNow = applyResponse(response)
    if (isDoneNow) {
      const finalResponse = await getPayAllProgress(cookies.lottmekong)
      if (finalResponse) {
        applyResponse(finalResponse)
      }
      setProgressComplete(true)
    }
  }, [cookies.lottmekong])

  const _onPayAll = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await payAllReward(cookies.lottmekong)
      if (response && response.ok) {
        batchTotalCountRef.current = typeof response.enqueued === 'number' ? response.enqueued : 0
        batchTotalNetRef.current = response.totalNetAmount || '0'
        setProgress({
          pendingCount: batchTotalCountRef.current,
          pendingAmount: batchTotalNetRef.current,
        })
        setDone(true)
      } else {
        setError('Không thể gửi yêu cầu trả thưởng')
      }
    } catch (e) {
      setError('Không thể gửi yêu cầu trả thưởng')
    } finally {
      setIsLoading(false)
    }
  }, [cookies.lottmekong])

  useEffect(() => {
    if (!prevShow.current && props.show) {
      prevShow.current = true
      setDone(false)
      setProgressComplete(false)
      setProgress({ pendingCount: 0, pendingAmount: '0' })
      batchTotalCountRef.current = 0
      batchTotalNetRef.current = '0'
      setError('')
      _onPayAll()
      return
    }

    if (prevShow.current && !props.show) {
      prevShow.current = false
      setDone(false)
      setProgressComplete(false)
      setError('')
    }
  }, [props.show, _onPayAll])

  useEffect(() => {
    if (!props.show || !done || progressComplete) {
      return
    }
    _fetchProgress()
    const timer = setInterval(() => {
      _fetchProgress()
    }, 2000)
    return () => clearInterval(timer)
  }, [props.show, done, progressComplete, _fetchProgress])

  const _onHide = () => {
    if (isLoading) {
      alert('Đang gửi yêu cầu trả thưởng')
      return
    }
    props.onHide(progressComplete)
  }

  const totalTrans = batchTotalCountRef.current
  const totalNetStr = batchTotalNetRef.current

  const handledStats = useMemo(() => {
    const pending = progress.pendingCount
    const pendingAmt = progress.pendingAmount || '0'
    const handledCount = Math.min(totalTrans, Math.max(0, totalTrans - pending))
    let handledMoneyStr = '0'
    try {
      const totalB = BigInt(totalNetStr || '0')
      const pendingB = BigInt(pendingAmt || '0')
      let diff = totalB - pendingB
      if (diff < BigInt(0)) diff = BigInt(0)
      if (diff > totalB) diff = totalB
      handledMoneyStr = diff.toString()
    } catch {
      handledMoneyStr = '0'
    }
    return { handledCount, handledMoneyStr }
  }, [progress.pendingCount, progress.pendingAmount, totalTrans, totalNetStr])

  const bodyDone = done && !isLoading && !error

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={_onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontWeight: 'normal', fontSize: 15 }} id="contained-modal-title-vcenter">
          Đang trả thưởng
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && 'Đang gửi yêu cầu trả thưởng...'}
        {!isLoading && !done && error}
        {bodyDone && (
          <>
            {!progressComplete && <div className="text-muted mb-2">Đang xử lý…</div>}
            {progressComplete && (
              <div className="text-success mb-2">
                <b>Hoàn tất trả thưởng</b>
              </div>
            )}
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Tiền cần trả (còn lại)</td>
                  <td className="text-end fw-bold">{formatVnd(progress.pendingAmount)} VND</td>
                </tr>
                <tr>
                  <td className="text-muted">Số giao dịch cần xử lý</td>
                  <td className="text-end fw-bold">{progress.pendingCount}</td>
                </tr>
                <tr>
                  <td className="text-muted">Tổng tiền (đợt này)</td>
                  <td className="text-end fw-bold">{formatVnd(totalNetStr)} VND</td>
                </tr>
                <tr>
                  <td className="text-muted">Đã xử lý</td>
                  <td className="text-end fw-bold">
                    {handledStats.handledCount} giao dịch — {formatVnd(handledStats.handledMoneyStr)} VND
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={_onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  )
}
